'use client'

/**
 * SearchLocation
 *
 * Client-side location input used by provider search pages. Responsibilities:
 * - Provide a single controlled input for ZIP, neighborhood, or city text.
 * - Use the Sharp-style Places autocomplete and geocoder hooks for live input.
 * - Expose `onLocationChange(coords, text)` so parent wrappers can apply
 *   Algolia `aroundLatLng` via `Configure` when appropriate.
 * - Support browser geolocation ("Use my current location") with graceful
 *   degradation if permission is denied or the API times out.
 * - Avoid duplicate location work for the same live suggestion or exact
 *   selection and show loading state while requests are in-flight.
 *
 * Keeping this as a focused client component keeps location concerns local
 * to the search UI and avoids introducing global stores or cross-cutting
 * side-effects. The component intentionally does not apply Algolia
 * configuration itself — it reports coordinates to the parent which may
 * choose how to apply them (page wrapper vs local Configure).
 */

import { useState, type SyntheticEvent, useEffect, useId, useRef } from 'react'
import { Autocomplete, Icon } from '@shc/ui'
import {
  getBrowserLocation,
  isScmgGeoData,
  type Coordinates,
  type GeoData,
  type ResolvedLocation,
} from '@lib/utilities/location'
import { type PlacePrediction, useAutocompleteSuggestion } from '@hooks/use-autocomplete-suggestion'
import { useGeocoder } from '@hooks/use-geocoder'
import debounce from 'lodash/debounce'

const INPUT_DEBOUNCE_MS = 200
const SearchAutocomplete = Autocomplete as any

type SearchLocationOption = {
  kind: 'current-location' | 'suggestion'
  label: string
  placeId: string | null
}

const CURRENT_LOCATION_OPTION: SearchLocationOption = {
  kind: 'current-location',
  label: 'Current location',
  placeId: null,
}

const getOptionSelectionKey = (option: SearchLocationOption) => `${option.kind}:${option.placeId ?? option.label}`

type ApplyResolvedLocationOptions = {
  keepInputValue?: boolean
  keepSelectedOption?: boolean
  closeDropdown?: boolean
  nextQueryValue?: string
  option?: SearchLocationOption | null
}

type ResolveSelectedOptionOptions = {
  keepInputValue?: boolean
  keepSelectedOption?: boolean
  keepDropdownOpen?: boolean
}

type SearchLocationProps = {
  onLocationChange?: (coords: Coordinates | null, locationText: string) => void
}

const toResolvedLocation = (
  geoData: GeoData | null,
  fallbackLabel: string,
): ResolvedLocation | null => {
  if (!geoData || typeof geoData.lat !== 'number' || typeof geoData.lng !== 'number') {
    return null
  }

  const formattedAddress = geoData.formattedAddress ?? fallbackLabel

  return {
    lat: geoData.lat,
    lng: geoData.lng,
    label: formattedAddress,
    location: geoData.postalCode ?? formattedAddress,
    formattedAddress,
    geoData,
  }
}

const SearchLocation = ({ onLocationChange }: SearchLocationProps) => {
  const inputId = useId()
  const [queryValue, setQueryValue] = useState('')
  const [selectedOption, setSelectedOption] = useState<SearchLocationOption | null>(null)
  const [options, setOptions] = useState<SearchLocationOption[]>([CURRENT_LOCATION_OPTION])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [isResolvingSelection, setIsResolvingSelection] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { getPredictions, loadError: predictionsLoadError } = useAutocompleteSuggestion()
  const {
    geocodeByAddress,
    geocodeByCoordinates,
    geocodeById,
    loadError: geocodeLoadError,
  } = useGeocoder()
  const inputRef = useRef<HTMLInputElement>(null)
  const initialHydrationRef = useRef(false)
  const latestSuggestionRequestIdRef = useRef(0)
  const latestResolutionRequestIdRef = useRef(0)
  const activeSelectionKeyRef = useRef<string | null>(null)
  const shouldAutoApplySuggestionsRef = useRef(false)
  const lastAppliedLocationRef = useRef<ResolvedLocation | null>(null)

  const buildOptions = (predictions: PlacePrediction[]) => {
    if (predictions.length === 0) {
      return [CURRENT_LOCATION_OPTION]
    }

    return [
      CURRENT_LOCATION_OPTION,
      ...predictions
        .filter((prediction) => Boolean(prediction.placeId && prediction.text?.text))
        .map((prediction) => ({
          kind: 'suggestion' as const,
          label: prediction.text.text,
          placeId: prediction.placeId,
        })),
    ]
  }

  const clearAppliedLocation = () => {
    lastAppliedLocationRef.current = null
    onLocationChange?.(null, '')
  }

  const applyResolvedLocation = (
    location: ResolvedLocation,
    {
      keepInputValue = false,
      keepSelectedOption = false,
      closeDropdown = true,
      nextQueryValue = location.formattedAddress ?? location.label ?? queryValue,
      option = null,
    }: ApplyResolvedLocationOptions = {},
  ) => {
    const locationText = location.formattedAddress ?? location.label ?? nextQueryValue

    if (!keepSelectedOption) {
      setSelectedOption(option)
    }

    if (!keepInputValue) {
      setQueryValue(nextQueryValue)
    }

    setIsDropdownOpen(!closeDropdown)
    lastAppliedLocationRef.current = location
    onLocationChange?.(
      {
        lat: location.lat,
        lng: location.lng,
        label: location.label,
        location: location.location ?? locationText,
      },
      locationText,
    )
  }

  const handleUseMyLocation = async () => {
    const requestId = ++latestResolutionRequestIdRef.current
    setIsGettingLocation(true)

    try {
      const coords = await getBrowserLocation()
      if (!coords || latestResolutionRequestIdRef.current !== requestId) {
        return
      }

      if (!geocodeByCoordinates) {
        return
      }

      const geoData = await geocodeByCoordinates({ ...coords })
      if (!geoData || latestResolutionRequestIdRef.current !== requestId || !isScmgGeoData(geoData)) {
        return
      }

      const location = toResolvedLocation(geoData, CURRENT_LOCATION_OPTION.label)
      if (!location) {
        return
      }

      shouldAutoApplySuggestionsRef.current = false

      applyResolvedLocation(location, {
        nextQueryValue: geoData.formattedAddress ?? CURRENT_LOCATION_OPTION.label,
        option: CURRENT_LOCATION_OPTION,
      })
    } finally {
      if (latestResolutionRequestIdRef.current === requestId) {
        setIsGettingLocation(false)
      }
    }
  }

  const resolveSelectedOption = async (
    option: SearchLocationOption,
    {
      keepInputValue = false,
      keepSelectedOption = false,
      keepDropdownOpen = false,
    }: ResolveSelectedOptionOptions = {},
  ) => {
    const selectionKey = getOptionSelectionKey(option)
    if (activeSelectionKeyRef.current === selectionKey) {
      return
    }

    activeSelectionKeyRef.current = selectionKey

    try {
      if (option.kind === 'current-location') {
        await handleUseMyLocation()
        return
      }

      if (!option.placeId) {
        return
      }

      const requestId = ++latestResolutionRequestIdRef.current
      setIsResolvingSelection(true)

      try {
        if (!geocodeById) {
          return
        }

        const geoData = await geocodeById(option.placeId)
        const location = toResolvedLocation(geoData, option.label)
        if (!location || latestResolutionRequestIdRef.current !== requestId) {
          if (latestResolutionRequestIdRef.current === requestId && keepInputValue) {
            clearAppliedLocation()
          }
          return
        }

        shouldAutoApplySuggestionsRef.current = false

        applyResolvedLocation(location, {
          keepInputValue,
          keepSelectedOption,
          closeDropdown: !keepDropdownOpen,
          nextQueryValue: location.formattedAddress ?? option.label,
          option,
        })
      } finally {
        if (latestResolutionRequestIdRef.current === requestId) {
          setIsResolvingSelection(false)
        }
      }
    } finally {
      if (activeSelectionKeyRef.current === selectionKey) {
        activeSelectionKeyRef.current = null
      }
    }
  }

  const handleQueryValueChange = (
    _event: SyntheticEvent<Element, Event> | null,
    value: string,
    reason: string,
  ) => {
    if (reason === 'clear') {
      shouldAutoApplySuggestionsRef.current = false
      setSelectedOption(null)
      setQueryValue('')
      setOptions([CURRENT_LOCATION_OPTION])
      clearAppliedLocation()
      return
    }

    if (reason === 'reset') {
      shouldAutoApplySuggestionsRef.current = false
      setQueryValue(value)
      return
    }

    if (reason === 'selectOption') {
      shouldAutoApplySuggestionsRef.current = false
      setQueryValue(value)

      // The underlying autocomplete callback only exposes the rendered string here,
      // so this fallback matches the current option list when onChange does not fire.
      const selectedSuggestion = options.find((option) => option.label === value)
      if (selectedSuggestion) {
        void resolveSelectedOption(selectedSuggestion)
      }
      return
    }

    if (reason !== 'input') {
      return
    }

    setIsDropdownOpen(true)
    setSelectedOption(null)
    setQueryValue(value)

    if (!value.trim()) {
      shouldAutoApplySuggestionsRef.current = false
      setOptions([CURRENT_LOCATION_OPTION])
      clearAppliedLocation()
      return
    }

    shouldAutoApplySuggestionsRef.current = true
  }

  const handleOptionChange = async (
    _event: SyntheticEvent,
    option: SearchLocationOption | null,
  ) => {
    if (!option) {
      return
    }

    await resolveSelectedOption(option)
  }

  useEffect(() => {
    if (!getPredictions && !predictionsLoadError) {
      return
    }

    const trimmedQuery = queryValue.trim()
    if (!trimmedQuery) {
      setOptions([CURRENT_LOCATION_OPTION])
      setIsLoadingSuggestions(false)
      return
    }

    const requestId = ++latestSuggestionRequestIdRef.current
    const debouncedFetch = debounce(async () => {
      setIsLoadingSuggestions(true)

      const predictions = getPredictions ? await getPredictions(trimmedQuery) : []

      if (latestSuggestionRequestIdRef.current !== requestId) {
        return
      }

      setOptions(buildOptions(predictions))
      setIsLoadingSuggestions(false)

      if (!shouldAutoApplySuggestionsRef.current) {
        return
      }

      if (predictions.length === 0) {
        clearAppliedLocation()
        return
      }

      const firstSuggestion = predictions[0]
      if (!firstSuggestion?.placeId || !firstSuggestion.text?.text) {
        clearAppliedLocation()
        return
      }

      if (lastAppliedLocationRef.current?.geoData?.id === firstSuggestion.placeId) {
        return
      }

      await resolveSelectedOption(
        {
          kind: 'suggestion',
          label: firstSuggestion.text.text,
          placeId: firstSuggestion.placeId,
        },
        {
          keepInputValue: true,
          keepSelectedOption: true,
          keepDropdownOpen: true,
        },
      )
    }, INPUT_DEBOUNCE_MS)

    debouncedFetch()

    return () => {
      debouncedFetch.cancel()
    }
  }, [getPredictions, predictionsLoadError, queryValue])

  useEffect(() => {
    if (!geocodeByAddress && !geocodeLoadError) {
      return
    }

    const syncLocationFromUrl = () => {
      const initialValue =
        typeof globalThis.window === 'undefined'
          ? null
          : new URL(globalThis.window.location.href).searchParams.get('l')?.trim() ?? null

      if (!initialValue) {
        shouldAutoApplySuggestionsRef.current = false
        setSelectedOption(null)
        setQueryValue('')
        setOptions([CURRENT_LOCATION_OPTION])

        if (lastAppliedLocationRef.current) {
          clearAppliedLocation()
        }

        return
      }

      const requestId = ++latestResolutionRequestIdRef.current
      shouldAutoApplySuggestionsRef.current = false
      setQueryValue(initialValue)
      setIsResolvingSelection(true)

      geocodeByAddress?.(initialValue)
        .then((geoData) => {
          const location = toResolvedLocation(geoData ?? null, initialValue)
          if (!location || latestResolutionRequestIdRef.current !== requestId) {
            setSelectedOption(null)
            setQueryValue(initialValue)
            setOptions([CURRENT_LOCATION_OPTION])
            clearAppliedLocation()
            return
          }

          applyResolvedLocation(location, {
            nextQueryValue: location.formattedAddress ?? location.label ?? initialValue,
          })
        })
        .finally(() => {
          if (latestResolutionRequestIdRef.current === requestId) {
            setIsResolvingSelection(false)
          }
        })
    }

    if (!initialHydrationRef.current) {
      initialHydrationRef.current = true
      syncLocationFromUrl()
    }

    globalThis.window.addEventListener('popstate', syncLocationFromUrl)

    return () => {
      globalThis.window.removeEventListener('popstate', syncLocationFromUrl)
    }
  }, [geocodeByAddress, geocodeLoadError, onLocationChange, queryValue])

  useEffect(() => {
    const input = inputRef.current

    if (!input) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false)
        return
      }

      if (event.key !== 'Enter') {
        return
      }

      if (input.getAttribute('aria-activedescendant')) {
        return
      }

      event.preventDefault()
      setIsDropdownOpen(false)

      queueMicrotask(() => {
        input.focus()
      })
    }

    input.addEventListener('keydown', handleKeyDown)

    return () => {
      input.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <div className="col-span-12 md:col-span-6 xl:col-span-4">
      <label htmlFor={inputId} className="sr-only">
        Location
      </label>
      <SearchAutocomplete
        id={inputId}
        ref={inputRef}
        className='input-reset fad-location-autocomplete'
        data-testid={'location-search'}
        freeSolo
        clearOnBlur={false}
        inputValue={queryValue}
        value={selectedOption as any}
        aria-label="Location"
        open={isDropdownOpen}
        onOpen={() => setIsDropdownOpen(true)}
        onClose={() => setIsDropdownOpen(false)}
        onChange={handleOptionChange as any}
        placeholder="City, neighborhood or ZIP code"
        options={options as any[]}
        disabled={isGettingLocation}
        onInputChange={handleQueryValueChange as any}
        isOptionEqualToValue={(option: SearchLocationOption, value: SearchLocationOption) =>
          option.kind === value.kind && option.placeId === value.placeId && option.label === value.label
        }
        getOptionLabel={(option: string | SearchLocationOption) =>
          typeof option === 'string' ? option : option.label
        }
        startDecorator={<Icon className="h-3.5" icon="location-dot" />}
        endDecorator={(isLoadingSuggestions || isResolvingSelection || isGettingLocation) && <Icon icon="spinner-third" spin />}
        filterOptions={(inputOptions: any) => inputOptions}
      />
    </div>
  )
}

export default SearchLocation